import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorBlock } from '../components/ErrorBlock';
import { Layout } from '../components/Layout';
import { Loading } from '../components/Loading';
import { Toasts } from '../components/Toasts';
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage';
import { LoginPage } from '../features/auth/LoginPage';
import { NotPartnerPage } from '../features/auth/NotPartnerPage';
import { CaseNewPage } from '../features/cases/CaseNewPage';
import { CasePage } from '../features/cases/CasePage';
import { CasesPage } from '../features/cases/CasesPage';
import { HandoverPage } from '../features/handovers/HandoverPage';
import { HandoversPage } from '../features/handovers/HandoversPage';
import { HomePage } from '../features/home/HomePage';
import { SettingsPage } from '../features/more/SettingsPage';
import { TripsPage } from '../features/trips/TripsPage';
import { VehiclesPage } from '../features/trips/VehiclesPage';
import { startI18nRefresh, useI18n, useT } from '../i18n';
import { startSessionSync, useSession } from './session';

function Gate({ children }: { children?: ReactNode }) {
  const status = useSession((s) => s.status);
  const load = useSession((s) => s.load);
  const location = useLocation();
  if (status === 'unknown' || status === 'loading') return <Loading />;
  if (status === 'error') return <ErrorBlock error={null} onRetry={() => void load()} />;
  if (status === 'anon') return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (status === 'not_partner') return <Navigate to="/not-partner" replace />;
  if (status === 'must_change') return <ChangePasswordPage forced />;
  return <>{children ?? <Outlet />}</>;
}

function AnonOnly({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status);
  if (status === 'authed') return <Navigate to="/" replace />;
  if (status === 'must_change') return <Navigate to="/password" replace />;
  return <>{children}</>;
}

function Shell() {
  return (
    <Gate>
      <Layout>
        <Outlet />
      </Layout>
    </Gate>
  );
}

function DocumentMeta() {
  const locale = useI18n((s) => s.locale);
  const t = useT();
  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = t('app.name');
  }, [locale, t]);
  return null;
}

function NotFound() {
  const t = useT();
  return <p className="py-10 text-txt">{t('err.not_found')}</p>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <AnonOnly>
            <LoginPage />
          </AnonOnly>
        }
      />
      <Route path="/not-partner" element={<NotPartnerPage />} />
      <Route element={<Shell />}>
        <Route index element={<HomePage />} />
        <Route path="handovers" element={<HandoversPage />} />
        <Route path="handovers/:id" element={<HandoverPage />} />
        <Route path="cases" element={<CasesPage />} />
        <Route path="cases/new" element={<CaseNewPage />} />
        <Route path="cases/:id" element={<CasePage />} />
        <Route path="trips" element={<TripsPage />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="more" element={<SettingsPage />} />
        <Route path="password" element={<ChangePasswordPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export function App() {
  const load = useSession((s) => s.load);
  useEffect(() => {
    void load();
    const stopI18n = startI18nRefresh();
    const stopSync = startSessionSync();
    return () => {
      stopI18n();
      stopSync();
    };
  }, [load]);
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <DocumentMeta />
      <Toasts />
      <AppRoutes />
    </BrowserRouter>
  );
}
